<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { stageColor } from 'dashboard/components-next/Contacts/contactStageColors';

// "Prospecto" / "Cotizado" chip backed by a list-type contact attribute.
// Clicking it opens the attribute's values so the stage can be changed in
// place. Two looks: the coloured `chip` (summary card, conversation header
// title) and the neutral `button` (conversation header actions).
const props = defineProps({
  attribute: { type: Object, default: null },
  value: { type: String, default: '' },
  showDot: { type: Boolean, default: false },
  showChevron: { type: Boolean, default: false },
  variant: { type: String, default: 'chip' }, // chip | button
  align: { type: String, default: 'left' }, // dropdown side: left | right
});

const emit = defineEmits(['update']);

const { t } = useI18n();
const [isOpen, toggleOpen] = useToggle();

const color = computed(() => stageColor(props.value));
const isButton = computed(() => props.variant === 'button');

const label = computed(
  () =>
    props.value ||
    t('CONTACTS_LAYOUT.DETAILS.STAGE_CHIP.SELECT', {
      name: props.attribute?.attributeDisplayName,
    })
);

const triggerClass = computed(() => {
  if (isButton.value) {
    return 'h-8 px-[11px] rounded-[7px] bg-n-alpha-2 text-[12.5px] font-medium text-n-slate-12 hover:bg-n-alpha-3';
  }
  return props.value
    ? 'h-[22px] px-2 rounded-md text-xs font-semibold hover:opacity-80'
    : 'h-[22px] px-2 rounded-md text-xs font-semibold border border-dashed border-n-slate-7 text-n-slate-10';
});
const triggerStyle = computed(() =>
  !isButton.value && props.value
    ? { backgroundColor: color.value.bg, color: color.value.text }
    : undefined
);

const menuItems = computed(() =>
  (props.attribute?.attributeValues || []).map(option => ({
    label: option,
    value: option,
    action: 'select',
    isSelected: option === props.value,
  }))
);

const onAction = ({ value }) => {
  toggleOpen(false);
  if (value !== props.value) emit('update', value);
};
</script>

<template>
  <div
    v-if="attribute"
    v-on-clickaway="() => toggleOpen(false)"
    class="relative"
  >
    <button
      type="button"
      class="inline-flex items-center gap-1.5 max-w-full transition-colors"
      :class="triggerClass"
      :style="triggerStyle"
      @click="toggleOpen()"
    >
      <span
        v-if="(showDot || isButton) && value"
        class="rounded-full flex-shrink-0"
        :class="isButton ? 'size-[7px]' : 'size-1.5'"
        :style="{ backgroundColor: color.dot }"
      />
      <span class="truncate">{{ label }}</span>
      <svg
        v-if="showChevron"
        width="11"
        height="11"
        viewBox="0 0 14 14"
        class="flex-shrink-0 opacity-60"
      >
        <path
          d="M3.5 5.5 7 9l3.5-3.5"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    </button>
    <DropdownMenu
      v-if="isOpen"
      :menu-items="menuItems"
      show-search
      class="z-30 w-48 mt-1.5 top-full"
      :class="align === 'right' ? 'ltr:right-0 rtl:left-0' : 'ltr:left-0 rtl:right-0'"
      @action="onAction"
    />
  </div>
</template>
